import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import ComponentCard from "../../components/common/ComponentCard";
import Form from "../../components/form/Form";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Select from "../../components/form/Select";
import Button from "../../components/ui/button/Button";

const apiUrl = process.env.REACT_APP_API_URL;

const resolveOutletId = (outlet: any): string =>
  String(
    outlet?.venueoutlet_id ??
      outlet?.venue_outlet_id ??
      outlet?.outlet_id ??
      outlet?.id ??
      ""
  );

const resolveOutletName = (outlet: any): string =>
  String(outlet?.venue_outlet_name ?? outlet?.name ?? "");

const resolveStatus = (outlet: any): string => {
  const raw = outlet?.status;
  if (raw === 1 || raw === "1" || raw === "current") return "current";
  if (raw === 2 || raw === "2" || raw === "non-current" || raw === "non_current") {
    return "non-current";
  }
  return "current";
};

export default function UpdateVenueOutlet() {
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = location.state?.outlet;

  const [formData, setFormData] = useState({
    venueOutletName: "",
    status: "current",
  });
  const [errors, setErrors] = useState({
    venueOutletName: "",
  });
  const [submitError, setSubmitError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const outletId = resolveOutletId(outlet);

  useEffect(() => {
    if (!outlet) return;
    setFormData({
      venueOutletName: resolveOutletName(outlet),
      status: resolveStatus(outlet),
    });
  }, [outlet]);

  const validate = () => {
    const nextErrors = { venueOutletName: "" };
    if (!formData.venueOutletName.trim()) {
      nextErrors.venueOutletName = "Venue outlet name is required.";
    }
    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => !value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!outletId) {
      setSubmitError("No venue outlet selected. Please open an outlet from the list.");
      return;
    }
    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitError("");
    setSubmitLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        venueoutlet_id: Number.isNaN(Number(outletId)) ? outletId : Number(outletId),
        name: formData.venueOutletName,
        status: formData.status,
      };
      // console.log(payload);
    
      const response = await fetch(`${apiUrl}/venue_outlets`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || (result && result.success === false)) {
        throw new Error(result?.message || responseText || "Failed to update venue outlet.");
      }

      navigate("/venue-outlets");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to update venue outlet.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!outletId) {
      setSubmitError("No venue outlet selected. Please open an outlet from the list.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this venue outlet?")) return;
    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitError("");
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/venue_outlets/${outletId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });

      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || (result && result.success === false)) {
        throw new Error(result?.message || responseText || "Failed to delete venue outlet.");
      }

      navigate("/venue-outlets");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to delete venue outlet.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ComponentCard title="Update Venue Outlet">
      <Form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          <div className="col-span-full">
            <h4 className="border-b border-gray-200 pb-4 text-base font-medium text-gray-800 dark:border-gray-800 dark:text-white/90">
              Venue Outlet
            </h4>
          </div>

          <div>
            <Label>Venue Outlet Name</Label>
            <Input
              type="text"
              id="venueOutletName"
              name="venueOutletName"
              value={formData.venueOutletName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFormData((prev) => ({ ...prev, venueOutletName: e.target.value }));
                setErrors((prev) => ({ ...prev, venueOutletName: "" }));
              }}
            />
            {errors.venueOutletName && <p className="text-sm text-red-500">{errors.venueOutletName}</p>}
          </div>

          <div>
            <Label>Record Status</Label>
            <Select
              options={[
                { value: "current", label: "Current" },
                { value: "non-current", label: "Non-Current" },
              ]}
              value={formData.status}
              onChange={(value: string) => setFormData((prev) => ({ ...prev, status: value }))}
              className="mt-2 bg-gray-50 dark:bg-gray-800"
            />
          </div>

          {submitError && <p className="text-sm text-red-500">{submitError}</p>}

          <div className="flex gap-3">
            <Button size="sm" type="submit" disabled={submitLoading || !outletId}>
              {submitLoading ? "Updating..." : "Update Changes"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={() => navigate("/venue-outlets")}
            >
              Cancel
            </Button>
            <button
              type="button"
              className="rounded-lg bg-error-800 px-4 py-3 text-sm font-medium text-white shadow-theme-xs hover:bg-error-600 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleDelete}
              disabled={deleteLoading || submitLoading || !outletId}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Form>
    </ComponentCard>
  );
}
